import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'

import {dispatchKeys} from '../query-keys'
import {
    createWorkerAbsence,
    deleteWorkerAbsence,
    getDriversForDay,
    listOutOfServiceEquipment,
    listPublications,
    listSummaries,
    listWorkerAbsences,
    markBackInService,
    markOutOfService,
    previewSchedule,
    publishSchedule,
    sendSummary,
} from './api'
import type {
    CreateAbsenceInput,
    MarkOutOfServiceInput,
    PublishScheduleInput,
    SendSummaryInput,
} from './schemas'

export function useDriversForDay(date: string) {
    return useQuery({
        queryKey: dispatchKeys.driversForDay(date),
        queryFn: () => getDriversForDay(date),
    })
}

export function useWorkerAbsences(workerId: string | null) {
    return useQuery({
        queryKey: dispatchKeys.workerAbsences(workerId ?? ''),
        queryFn: () => listWorkerAbsences(workerId ?? ''),
        enabled: workerId != null,
    })
}

export function useOutOfServiceEquipment() {
    return useQuery({
        queryKey: dispatchKeys.outOfService(),
        queryFn: listOutOfServiceEquipment,
    })
}

export function usePublications(boardDate: string) {
    return useQuery({
        queryKey: dispatchKeys.publicationsForDay(boardDate),
        queryFn: () => listPublications(boardDate),
    })
}

export function useSummaries(boardDate: string, {enabled}: {enabled: boolean}) {
    return useQuery({
        queryKey: dispatchKeys.summariesForDay(boardDate),
        queryFn: () => listSummaries(boardDate),
        enabled,
    })
}

function useInvalidateDriverAvailability() {
    const queryClient = useQueryClient()

    return () => {
        void queryClient.invalidateQueries({queryKey: dispatchKeys.drivers()})
        void queryClient.invalidateQueries({queryKey: dispatchKeys.absences()})
        void queryClient.invalidateQueries({queryKey: dispatchKeys.availabilityBoard()})
    }
}

export function useAddAbsence() {
    const invalidate = useInvalidateDriverAvailability()

    return useMutation({
        mutationFn: ({workerId, input}: {workerId: string; input: CreateAbsenceInput}) =>
            createWorkerAbsence(workerId, input),
        onSuccess: invalidate,
    })
}

export function useRemoveAbsence() {
    const invalidate = useInvalidateDriverAvailability()

    return useMutation({
        mutationFn: (absenceId: string) => deleteWorkerAbsence(absenceId),
        onSuccess: invalidate,
    })
}

function useInvalidateOutOfService() {
    const queryClient = useQueryClient()

    return () => {
        void queryClient.invalidateQueries({queryKey: dispatchKeys.outOfService()})
        void queryClient.invalidateQueries({queryKey: dispatchKeys.availabilityBoard()})
    }
}

export function useMarkOutOfService() {
    const invalidate = useInvalidateOutOfService()

    return useMutation({
        mutationFn: ({
            rentalAssetId,
            input,
        }: {
            rentalAssetId: string
            input: MarkOutOfServiceInput
        }) => markOutOfService(rentalAssetId, input),
        onSuccess: invalidate,
    })
}

export function useBackInService() {
    const invalidate = useInvalidateOutOfService()

    return useMutation({
        mutationFn: (rentalAssetId: string) => markBackInService(rentalAssetId),
        onSuccess: invalidate,
    })
}

export function usePublishSchedule() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (input: PublishScheduleInput) => publishSchedule(input),
        onSuccess: () => {
            void queryClient.invalidateQueries({queryKey: dispatchKeys.publications()})
        },
    })
}

export function usePreviewSchedule() {
    return useMutation({
        mutationFn: (input: PublishScheduleInput) => previewSchedule(input),
    })
}

export function useSendSummary() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (input: SendSummaryInput) => sendSummary(input),
        onSuccess: () => {
            void queryClient.invalidateQueries({queryKey: dispatchKeys.summaries()})
        },
    })
}
